import type { DocumentV1 } from '@alpod-ai-writer/backend';
import type { CursorData } from '@alpod-ai-writer/shared';
import type { HocuspocusProvider } from '@hocuspocus/provider';
import {
    bindFirst,
    createTSlatePlugin,
    type OmitFirst,
    type PluginConfig,
} from '@udecode/plate';
import type * as Y from 'yjs';

import { getAllFootnotes } from './api/getAllFootnotes';
import { insertFootnote } from './transforms/insertFootnote';
import { setFootnoteContents } from './transforms/setFootnoteContents';
import { withFootnotes } from './withFootnotes';
  
export type BaseFootnotesConfig = PluginConfig<
    'footnote',
    {
        provider?: HocuspocusProvider;
        document?: DocumentV1.Record;
        cursorData?: CursorData;
        footnoteOffsetField?: string;
        currentDocumentField?: string;
        documentFieldList: string[];
        _openedFootnoteId: string|null;
        _changeRev: number;
    },
    {
        footnotes: {
            getAll: OmitFirst<typeof getAllFootnotes>;
            getOffset: () => number;
            watchOffset: (callback: (offset: number) => void) => () => void;
            setFootnoteCount: (index: number) => void;
            open(id: string): void;
            close(): void;
            isOpened: (id: string) => boolean;
        }
    },
    {
        insert: {
            footnote: OmitFirst<typeof insertFootnote>;
        };
        footnotes: {
            setContents: OmitFirst<typeof setFootnoteContents>;
        }
    }
>;

export const BaseFootnotesPlugin = createTSlatePlugin<BaseFootnotesConfig>({
    key: 'footnote',
    node: { isElement: true, isInline: true, isVoid: true },
    options: {
        documentFieldList: [],
        _openedFootnoteId: null,
        _changeRev: 0,
    }
})
    .extendEditorApi<BaseFootnotesConfig['api']>(({ editor, getOption, setOption }) => ({
        footnotes: {
            getAll: bindFirst(getAllFootnotes, editor),
            getOffset: () => {
                const yjsProvider = getOption('provider');
                const footnoteOffsetField = getOption('footnoteOffsetField');
                const documentFieldList = getOption('documentFieldList');
                const currentDocumentField = getOption('currentDocumentField');
                
                return getFootnoteOffset(
                    yjsProvider?.document.getMap<number>(footnoteOffsetField),
                    currentDocumentField ?? '',
                    documentFieldList
                );
            },
            watchOffset: (callback: (offset: number) => void) => {
                const yjsProvider = getOption('provider');
                const footnoteOffsetField = getOption('footnoteOffsetField');
                const documentFieldList = getOption('documentFieldList');
                const currentDocumentField = getOption('currentDocumentField');

                const map = yjsProvider?.document.getMap<number>(footnoteOffsetField);

                const changeHandler = () => {
                    const offset = getFootnoteOffset(
                        map,
                        currentDocumentField ?? '',
                        documentFieldList
                    );

                    callback(offset);
                };

                map?.observe(changeHandler);
    
                return () => {
                    map?.unobserve(changeHandler);
                };
            },
            setFootnoteCount: (index: number) => {
                const yjsProvider = getOption('provider');
                const documentField = getOption('currentDocumentField');
                const footnoteOffsetField = getOption('footnoteOffsetField');

                yjsProvider?.document.getMap<number>(footnoteOffsetField).set(documentField ?? '', index);
            },
            open: (id: string) => {
                setOption('_openedFootnoteId', id);
            },
            close: () => {
                setOption('_openedFootnoteId', null);
            },
            isOpened: (id: string) => {
                const openedFootnoteId = getOption('_openedFootnoteId');
                return openedFootnoteId === id;
            },
        }
    }))
    .extendEditorTransforms<BaseFootnotesConfig['transforms']>(({ editor }) => ({
        insert: {
            footnote: bindFirst(insertFootnote, editor),
        },
        footnotes: {
            setContents: bindFirst(setFootnoteContents, editor),
        }
    }))
    .overrideEditor(withFootnotes);

function getFootnoteOffset(footnoteIndex: Y.Map<number>|undefined, currentFieldName: string, documentFieldList: string[]): number {
    let offset = 0;

    if (!documentFieldList.includes(currentFieldName)) {
        return offset;
    }

    for (let i = 0; i < documentFieldList.length; i++) {
        const field = documentFieldList[i];

        if (field === currentFieldName) {
            break;
        }

        const fieldOffset = footnoteIndex?.get(field) ?? 0;
        offset += fieldOffset;
    }

    return offset;
}
